import { useParams, Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@travellegacy/nexops-design-system';
import { Card, CardContent, CardHeader, CardTitle } from '@travellegacy/nexops-design-system';
import { Badge } from '@travellegacy/nexops-design-system';
import { Button } from '@travellegacy/nexops-design-system';
import { Input } from '@travellegacy/nexops-design-system';
import { Spinner } from '@travellegacy/nexops-design-system';
import { User, MapPin, Briefcase, DollarSign, TrendingUp, Calendar, Edit, Plus, X, Percent, ArrowLeft, MessageCircle } from 'lucide-react';
import { useEmployeeHCM, useSalaryHistory, usePerformanceInsights, useAddSalaryHistory } from '../hooks/useHCM';
import { formatDateShort, insightSentimentLabels } from '../types';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useAuth } from '@/shared/contexts/AuthContext';
import { buttonVariants } from '@travellegacy/nexops-design-system';
import { cn } from '@/lib/utils';
import { CommissionStatement } from '@/features/billing/components';
import { useCommissionRules } from '@/features/billing/hooks';
import { serviceTypeLabels, ServiceType } from '@/shared/types';
import { commissionsAPI } from '@/shared/lib/api';
import { formatCPF, formatPhoneDisplay, formatCEP, formatCurrency } from '@/shared/lib/utils';

export function EmployeeProfilePage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { data: employee, isLoading } = useEmployeeHCM(id);
  const { data: salaryHistory = [] } = useSalaryHistory(id);
  const { data: insights = [] } = usePerformanceInsights(id);
  const { data: commissionRules = [], isLoading: isLoadingRules } = useCommissionRules(id);
  const addSalaryMutation = useAddSalaryHistory();

  const isAdmin = user?.role === 'ADMIN';
  const queryClient = useQueryClient();

  // Pegar o salário mais recente
  const currentSalary = salaryHistory.length > 0 ? salaryHistory[0] : null;

  // Estado para o modal de ajuste salarial
  const [showSalaryModal, setShowSalaryModal] = useState(false);
  const [employeeSalary, setEmployeeSalary] = useState('');
  const [salaryReason, setSalaryReason] = useState('');
  const [salaryError, setSalaryError] = useState('');

  // Estado para o modal de comissões
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [editingServiceType, setEditingServiceType] = useState<ServiceType | null>(null);
  const [commissionFormData, setCommissionFormData] = useState({
    level1FixedValue: '',
    level2FixedValue: '',
    level3FixedValue: '',
    level1Percentage: '',
    level2Percentage: '',
    level3Percentage: '',
  });
  const [commissionError, setCommissionError] = useState('');

  // Mutation para salvar comissão
  const saveCommissionMutation = useMutation({
    mutationFn: async (data: {
      serviceType: ServiceType;
      isNational?: boolean | null;
      level1FixedValue: number;
      level2FixedValue: number;
      level3FixedValue: number;
      level1Percentage?: number;
      level2Percentage?: number;
      level3Percentage?: number;
    }) => {
      if (!id) throw new Error('Employee ID is required');
      return commissionsAPI.upsertRule(id, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['commission-rules', id] });
      setShowCommissionModal(false);
      setEditingServiceType(null);
      setCommissionFormData({
        level1FixedValue: '',
        level2FixedValue: '',
        level3FixedValue: '',
        level1Percentage: '',
        level2Percentage: '',
        level3Percentage: '',
      });
      setCommissionError('');
    },
    onError: (error) => {
      setCommissionError(error instanceof Error ? error.message : 'Erro ao salvar comissão');
    },
  });

  // Abrir modal para editar/adicionar comissão
  const handleOpenCommissionModal = (serviceType: ServiceType) => {
    setEditingServiceType(serviceType);

    // Preencher formulário se já existir regra para este serviço
    const existingRule = commissionRules.find(r => r.serviceType === serviceType);
    if (existingRule) {
      setCommissionFormData({
        level1FixedValue: existingRule.level1FixedValue.toString(),
        level2FixedValue: existingRule.level2FixedValue.toString(),
        level3FixedValue: existingRule.level3FixedValue.toString(),
        level1Percentage: existingRule.level1Percentage?.toString() || '10',
        level2Percentage: existingRule.level2Percentage?.toString() || '20',
        level3Percentage: existingRule.level3Percentage?.toString() || '25',
      });
    } else {
      setCommissionFormData({
        level1FixedValue: '',
        level2FixedValue: '',
        level3FixedValue: '',
        level1Percentage: '10',
        level2Percentage: '20',
        level3Percentage: '25',
      });
    }

    setShowCommissionModal(true);
    setCommissionError('');
  };

  // Salvar comissão
  const handleSaveCommission = async () => {
    if (!editingServiceType) return;

    // Validar valores fixos para todos os níveis
    const level1Fixed = parseFloat(commissionFormData.level1FixedValue || '0');
    const level2Fixed = parseFloat(commissionFormData.level2FixedValue || '0');
    const level3Fixed = parseFloat(commissionFormData.level3FixedValue || '0');

    if (level1Fixed < 0 || level2Fixed < 0 || level3Fixed < 0) {
      setCommissionError('Os valores fixos não podem ser negativos');
      return;
    }

    // Validar percentuais
    const level1Pct = parseFloat(commissionFormData.level1Percentage || '10');
    const level2Pct = parseFloat(commissionFormData.level2Percentage || '20');
    const level3Pct = parseFloat(commissionFormData.level3Percentage || '25');

    if (level1Pct < 0 || level1Pct > 100 || level2Pct < 0 || level2Pct > 100 || level3Pct < 0 || level3Pct > 100) {
      setCommissionError('Os percentuais devem estar entre 0 e 100');
      return;
    }

    await saveCommissionMutation.mutateAsync({
      serviceType: editingServiceType,
      level1FixedValue: level1Fixed,
      level2FixedValue: level2Fixed,
      level3FixedValue: level3Fixed,
      level1Percentage: level1Pct,
      level2Percentage: level2Pct,
      level3Percentage: level3Pct,
    });
  };

  const handleAddSalary = async () => {
    if (!id) return;

    const empSalary = parseFloat(employeeSalary.replace(/\D/g, '')) / 100;

    if (!empSalary || empSalary <= 0) {
      setSalaryError('Digite um salário válido para o colaborador');
      return;
    }

    try {
      await addSalaryMutation.mutateAsync({
        employeeId: id,
        data: {
          value: empSalary, // Manter compatibilidade
          employeeSalary: empSalary,
          reason: salaryReason || 'Ajuste salarial',
        },
      });
      setShowSalaryModal(false);
      setEmployeeSalary('');
      setSalaryReason('');
      setSalaryError('');
    } catch (error) {
      setSalaryError('Erro ao adicionar ajuste salarial');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Spinner size="lg" />
      </div>
    );
  }

  if (!employee) {
    return <div className="p-8 text-center">Colaborador não encontrado</div>;
  }

  // Criar dados do gráfico incluindo ponto inicial na admissão
  const salaryChartData = (() => {
    const chartData = salaryHistory.map((s) => ({
      date: new Date(s.createdAt).toLocaleDateString('pt-BR', {
        month: 'short',
        year: 'numeric',
        timeZone: 'UTC'
      }),
      salary: s.employeeSalary || s.value, // Salário do colaborador
    }));

    // Adicionar ponto inicial na data de admissão se houver histórico
    if (salaryHistory.length > 0 && employee.admissionDate) {
      const firstSalary = salaryHistory[salaryHistory.length - 1]; // Último item (mais antigo)
      const admissionPoint = {
        date: new Date(employee.admissionDate).toLocaleDateString('pt-BR', {
          month: 'short',
          year: 'numeric',
          timeZone: 'UTC'
        }),
        salary: firstSalary.employeeSalary || firstSalary.value,
      };

      // Adicionar no início se a data de admissão for diferente da primeira entrada
      const firstEntryDate = new Date(firstSalary.createdAt);
      const admissionDate = new Date(employee.admissionDate);

      if (admissionDate < firstEntryDate) {
        return [admissionPoint, ...chartData.reverse()];
      }

      return chartData.reverse();
    }

    return chartData.reverse();
  })();

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/people-core/hcm/employees')}
          className="mt-1"
          title="Voltar para lista de colaboradores"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold">{employee.user?.name}</h1>
          <p className="text-muted-foreground">{employee.user?.email}</p>
        </div>
      </div>

      <Tabs defaultValue="dados" className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="dados">Dados</TabsTrigger>
          <TabsTrigger value="salario">Salário</TabsTrigger>
          <TabsTrigger value="comissao">Comissão</TabsTrigger>
          <TabsTrigger value="performance">Performance</TabsTrigger>
        </TabsList>

        <TabsContent value="dados" className="space-y-4">
          {/* Card Unificado: Informações do Colaborador */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Informações do Colaborador</CardTitle>
                {isAdmin && (
                  <Link
                    to={`/people-core/hcm/employees/${id}/edit`}
                    className={cn(buttonVariants({ variant: 'outline', size: 'sm' }))}
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Editar
                  </Link>
                )}
              </div>
            </CardHeader>
            <CardContent>
              <div className="grid gap-6">
                {/* Dados Pessoais */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <User className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-base">Dados Pessoais</h3>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 pl-7">
                    <div>
                      <p className="text-sm text-muted-foreground">CPF</p>
                      <p className="text-sm font-medium">{formatCPF(employee.cpf)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">RG</p>
                      <p className="text-sm font-medium">{employee.rg || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Data de Nascimento</p>
                      <p className="text-sm font-medium">{employee.birthDate ? formatDateShort(employee.birthDate) : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Telefone</p>
                      <p className="text-sm font-medium">{formatPhoneDisplay(employee.phone)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Pix</p>
                      <p className="text-sm font-medium">{employee.pixKey || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Conta Bancária</p>
                      <p className="text-sm font-medium">{employee.bankAccount || 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t" />

                {/* Endereço */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-base">Endereço</h3>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 pl-7">
                    <div>
                      <p className="text-sm text-muted-foreground">CEP</p>
                      <p className="text-sm font-medium">{formatCEP(employee.zipCode)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Rua</p>
                      <p className="text-sm font-medium">{employee.street || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Número</p>
                      <p className="text-sm font-medium">{employee.number || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Complemento</p>
                      <p className="text-sm font-medium">{employee.complement || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Bairro</p>
                      <p className="text-sm font-medium">{employee.neighborhood || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Cidade/UF</p>
                      <p className="text-sm font-medium">{employee.city && employee.state ? `${employee.city}/${employee.state}` : 'N/A'}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t" />

                {/* Dados Contratuais */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Briefcase className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-base">Dados Contratuais</h3>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 pl-7">
                    <div>
                      <p className="text-sm text-muted-foreground">Data de Admissão</p>
                      <p className="text-sm font-medium">{employee.admissionDate ? formatDateShort(employee.admissionDate) : 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Tipo de Contrato</p>
                      <p className="text-sm font-medium">{employee.contractType || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Cargo</p>
                      <p className="text-sm font-medium">{employee.jobPosition?.name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Departamento</p>
                      <p className="text-sm font-medium">{employee.jobPosition?.department?.name || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Salário Atual</p>
                      <p className="text-sm font-medium">
                        {currentSalary
                          ? `R$ ${(currentSalary.employeeSalary || currentSalary.value).toLocaleString('pt-BR', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}`
                          : 'Não informado'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="border-t" />

                {/* Benefícios */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-base">Benefícios</h3>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 pl-7">
                    <div>
                      <p className="text-sm text-muted-foreground">VA/VR (Mensal)</p>
                      <p className="text-sm font-medium">
                        {employee.benefitVAVR
                          ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(employee.benefitVAVR)
                          : '-'
                        }
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Auxílio Home Office (Mensal)</p>
                      <p className="text-sm font-medium">
                        {employee.benefitHomeOffice
                          ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(employee.benefitHomeOffice)
                          : '-'
                        }
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">VT - Valor Fornecido (Mensal)</p>
                      <p className="text-sm font-medium">
                        {employee.benefitVT
                          ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(employee.benefitVT)
                          : '-'
                        }
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">VT - Desconto em Folha</p>
                      <p className="text-sm font-medium">
                        {employee.benefitVTDiscount
                          ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(employee.benefitVTDiscount)
                          : 'R$ 0,00'
                        }
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">VT - Pago via</p>
                      <p className="text-sm font-medium">
                        {employee.benefitVTPaymentMethod === 'PIX' ? 'Pix (junto do pagamento)' : 'Pluxee (cartão)'}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Gympass</p>
                      <p className="text-sm font-medium">{employee.hasGympass ? '✓ Ativo' : '✗ Não ativo'}</p>
                    </div>
                  </div>
                </div>

                <div className="border-t" />

                {/* Dados de Sócio */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <DollarSign className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-base">Dados de Sócio</h3>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 pl-7">
                    <div>
                      <p className="text-sm text-muted-foreground">É Sócio?</p>
                      <p className="text-sm font-medium">{employee.isPartner ? '✓ Sim' : '✗ Não'}</p>
                    </div>
                    {employee.isPartner && (
                      <>
                        <div>
                          <p className="text-sm text-muted-foreground">% de Participação</p>
                          <p className="text-sm font-medium">{employee.partnershipPercentage ? `${employee.partnershipPercentage}%` : '-'}</p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Antecipação Mensal</p>
                          <p className="text-sm font-medium">{employee.monthlyProfitAdvance ? formatCurrency(employee.monthlyProfitAdvance) : '-'}</p>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="border-t" />

                {/* Contato Corporativo */}
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <MessageCircle className="h-5 w-5 text-primary" />
                    <h3 className="font-semibold text-base">Contato Corporativo</h3>
                  </div>
                  <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3 pl-7">
                    <div>
                      <p className="text-sm text-muted-foreground">WhatsApp</p>
                      <p className="text-sm font-medium">{employee.user?.whatsapp || 'Não cadastrado'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-muted-foreground">Slack User ID</p>
                      <p className="text-sm font-medium">{employee.user?.slackUserId || 'Não cadastrado'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Card de Configurações de Comissão */}
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <Percent className="h-5 w-5" />
                  Configurações de Comissão
                </CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingRules ? (
                <div className="flex items-center justify-center py-8">
                  <Spinner />
                </div>
              ) : (
                <>
                  {commissionRules.length === 0 && (
                    <p className="text-center text-muted-foreground py-4 mb-4">Nenhuma regra de comissão configurada</p>
                  )}

                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {commissionRules.map((rule) => (
                      <div
                        key={rule.id}
                        className="border rounded-lg p-4 relative group hover:border-primary transition-colors cursor-pointer"
                        onClick={() => isAdmin && handleOpenCommissionModal(rule.serviceType)}
                      >
                        <p className="font-semibold text-base mb-3">{serviceTypeLabels[rule.serviceType]}</p>
                        <div className="space-y-2">
                          <div>
                            <p className="text-xs text-muted-foreground">Nível 1 (100% Dentro)</p>
                            <p className="text-sm font-medium">
                              R$ {rule.level1FixedValue.toFixed(2)} | {rule.level1Percentage}%
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Nível 2 (Cotação Dentro, Emissão Fora)</p>
                            <p className="text-sm font-medium">
                              R$ {rule.level2FixedValue.toFixed(2)} | {rule.level2Percentage}%
                            </p>
                          </div>
                          <div>
                            <p className="text-xs text-muted-foreground">Nível 3 (100% Fora)</p>
                            <p className="text-sm font-medium">
                              R$ {rule.level3FixedValue.toFixed(2)} | {rule.level3Percentage}%
                            </p>
                          </div>
                          {(rule.serviceType === 'FLIGHT' || rule.serviceType === 'FLIGHT_MILES') && rule.isNational !== null && (
                            <div className="pt-2 border-t">
                              <p className="text-xs text-muted-foreground">
                                {rule.isNational ? '✈️ Nacional' : '🌍 Internacional'}
                              </p>
                            </div>
                          )}
                        </div>
                        {isAdmin && (
                          <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <Edit className="h-4 w-4 text-primary" />
                          </div>
                        )}
                      </div>
                    ))}

                    {/* Adicionar novos serviços */}
                    {isAdmin && Object.values(ServiceType).map((serviceType) => {
                      const hasRule = commissionRules.some(r => r.serviceType === serviceType);
                      if (hasRule) return null;

                      return (
                        <button
                          key={serviceType}
                          onClick={() => handleOpenCommissionModal(serviceType)}
                          className="border-2 border-dashed rounded-lg p-4 hover:border-primary hover:bg-muted/50 transition-colors text-left"
                        >
                          <p className="font-semibold text-base mb-2">{serviceTypeLabels[serviceType]}</p>
                          <p className="text-sm text-muted-foreground flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            Adicionar configuração
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="salario" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Histórico Salarial
                </CardTitle>
                {isAdmin && (
                  <Button size="sm" onClick={() => setShowSalaryModal(true)}>
                    <Plus className="mr-2 h-4 w-4" />
                    Adicionar Ajuste
                  </Button>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {salaryHistory.length > 0 && (
                <div className="mb-6">
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={salaryChartData}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis
                        tickFormatter={(value) => {
                          if (value === 0) return 'R$ 0';
                          if (value >= 1000) {
                            return `R$ ${(value / 1000).toFixed(1)}k`;
                          }
                          return `R$ ${value}`;
                        }}
                        domain={[0, 'auto']}
                      />
                      <Tooltip
                        content={({ active, payload }) => {
                          if (active && payload && payload.length) {
                            const salary = payload.find(p => p.dataKey === 'salary')?.value;

                            return (
                              <div className="bg-background border border-border rounded-lg shadow-lg p-3">
                                <p className="text-sm font-medium mb-2">{payload[0].payload.date}</p>

                                {salary && (
                                  <div className="mb-1">
                                    <p className="text-xs text-muted-foreground">Salário Colaborador</p>
                                    <p className="text-base font-bold" style={{ color: '#8884d8' }}>
                                      R$ {Number(salary).toLocaleString('pt-BR', {
                                        minimumFractionDigits: 2,
                                        maximumFractionDigits: 2,
                                      })}
                                    </p>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        }}
                      />
                      <Legend
                        verticalAlign="top"
                        height={36}
                        formatter={(value) => {
                          if (value === 'salary') return 'Salário Colaborador';
                          return value;
                        }}
                      />
                      <Line
                        type="monotone"
                        dataKey="salary"
                        stroke="#8884d8"
                        strokeWidth={2}
                        name="Salário Colaborador"
                        dot={{ r: 4 }}
                        activeDot={{ r: 6 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              <div className="space-y-3">
                {salaryHistory.length === 0 ? (
                  <p className="text-center text-muted-foreground py-4">Nenhum histórico salarial registrado</p>
                ) : (
                  salaryHistory.map((s) => (
                    <div key={s.id} className="flex justify-between items-center border-b pb-2">
                      <div>
                        <p className="font-semibold">R$ {s.value.toLocaleString('pt-BR')}</p>
                        <p className="text-sm text-muted-foreground">{s.reason || 'Sem motivo'}</p>
                      </div>
                      <p className="text-sm text-muted-foreground">
                        {new Date(s.createdAt).toLocaleDateString('pt-BR')}
                      </p>
                    </div>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="comissao" className="space-y-4">
          <CommissionStatement employeeId={id!} />
        </TabsContent>

        <TabsContent value="performance" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <TrendingUp className="h-5 w-5" />
                Insights de Performance (IA)
              </CardTitle>
            </CardHeader>
            <CardContent>
              {insights.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">Nenhum insight registrado</p>
              ) : (
                <div className="space-y-4">
                  {insights.map((insight) => (
                    <div key={insight.id} className="border rounded-lg p-4">
                      <div className="flex items-start justify-between mb-2">
                        <Badge
                          variant={
                            insight.sentiment === 'POSITIVE'
                              ? 'success'
                              : insight.sentiment === 'NEGATIVE'
                              ? 'error'
                              : 'info'
                          }
                        >
                          {insightSentimentLabels[insight.sentiment]}
                        </Badge>
                        <p className="text-sm text-muted-foreground flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {new Date(insight.date).toLocaleDateString('pt-BR')}
                        </p>
                      </div>
                      <p className="text-sm mb-3">{insight.content}</p>
                      {insight.metadata?.score && (
                        <p className="text-sm font-semibold">
                          Nota: {insight.metadata.score.toFixed(1)}/10
                        </p>
                      )}
                      {insight.metadata?.highlights && insight.metadata.highlights.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-semibold mb-1">Destaques:</p>
                          <ul className="text-sm list-disc list-inside space-y-1">
                            {insight.metadata.highlights.map((h, i) => (
                              <li key={i}>{h}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {insight.metadata?.improvements && insight.metadata.improvements.length > 0 && (
                        <div className="mt-3">
                          <p className="text-sm font-semibold mb-1">Pontos de Melhoria:</p>
                          <ul className="text-sm list-disc list-inside space-y-1">
                            {insight.metadata.improvements.map((i, idx) => (
                              <li key={idx}>{i}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal de Ajuste Salarial */}
      {showSalaryModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background border rounded-lg shadow-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Adicionar Ajuste Salarial</h2>
              <button
                onClick={() => {
                  setShowSalaryModal(false);
                  setEmployeeSalary('');
                  setSalaryReason('');
                  setSalaryError('');
                }}
                className="p-1 hover:bg-muted rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <Input
                label="Salário do Colaborador (R$)"
                type="text"
                value={employeeSalary}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '');
                  const formatted = (parseFloat(value) / 100).toFixed(2);
                  setEmployeeSalary(formatted);
                  setSalaryError('');
                }}
                placeholder="0,00"
                error={salaryError}
              />

              <Input
                label="Motivo (opcional)"
                value={salaryReason}
                onChange={(e) => setSalaryReason(e.target.value)}
                placeholder="Ex: Promoção, Ajuste de mercado, etc."
              />

              {salaryError && (
                <p className="text-sm text-destructive">{salaryError}</p>
              )}

              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowSalaryModal(false);
                    setEmployeeSalary('');
                    setSalaryReason('');
                    setSalaryError('');
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleAddSalary}
                  disabled={addSalaryMutation.isPending}
                >
                  {addSalaryMutation.isPending ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Comissão */}
      {showCommissionModal && editingServiceType && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-background border rounded-lg shadow-lg p-6 w-full max-w-md">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">
                {commissionRules.some(r => r.serviceType === editingServiceType) ? 'Editar' : 'Adicionar'} Comissão - {serviceTypeLabels[editingServiceType]}
              </h2>
              <button
                onClick={() => {
                  setShowCommissionModal(false);
                  setEditingServiceType(null);
                  setCommissionFormData({
                    level1FixedValue: '',
                    level2FixedValue: '',
                    level3FixedValue: '',
                    level1Percentage: '10',
                    level2Percentage: '20',
                    level3Percentage: '25',
                  });
                  setCommissionError('');
                }}
                className="p-1 hover:bg-muted rounded"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4">
              <div className="space-y-3">
                <h3 className="font-semibold text-sm">Nível 1 - 100% Dentro do Horário</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Valor Fixo (R$)"
                    type="number"
                    step="0.01"
                    min="0"
                    value={commissionFormData.level1FixedValue}
                    onChange={(e) => {
                      setCommissionFormData({ ...commissionFormData, level1FixedValue: e.target.value });
                      setCommissionError('');
                    }}
                    placeholder="0.00"
                  />
                  <Input
                    label="Percentual (%)"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={commissionFormData.level1Percentage}
                    onChange={(e) => {
                      setCommissionFormData({ ...commissionFormData, level1Percentage: e.target.value });
                      setCommissionError('');
                    }}
                    placeholder="10"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-sm">Nível 2 - Cotação Dentro, Emissão Fora</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Valor Fixo (R$)"
                    type="number"
                    step="0.01"
                    min="0"
                    value={commissionFormData.level2FixedValue}
                    onChange={(e) => {
                      setCommissionFormData({ ...commissionFormData, level2FixedValue: e.target.value });
                      setCommissionError('');
                    }}
                    placeholder="0.00"
                  />
                  <Input
                    label="Percentual (%)"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={commissionFormData.level2Percentage}
                    onChange={(e) => {
                      setCommissionFormData({ ...commissionFormData, level2Percentage: e.target.value });
                      setCommissionError('');
                    }}
                    placeholder="20"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <h3 className="font-semibold text-sm">Nível 3 - 100% Fora do Horário</h3>
                <div className="grid grid-cols-2 gap-3">
                  <Input
                    label="Valor Fixo (R$)"
                    type="number"
                    step="0.01"
                    min="0"
                    value={commissionFormData.level3FixedValue}
                    onChange={(e) => {
                      setCommissionFormData({ ...commissionFormData, level3FixedValue: e.target.value });
                      setCommissionError('');
                    }}
                    placeholder="0.00"
                  />
                  <Input
                    label="Percentual (%)"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={commissionFormData.level3Percentage}
                    onChange={(e) => {
                      setCommissionFormData({ ...commissionFormData, level3Percentage: e.target.value });
                      setCommissionError('');
                    }}
                    placeholder="25"
                  />
                </div>
              </div>

              {commissionError && (
                <p className="text-sm text-destructive">{commissionError}</p>
              )}

              <div className="flex gap-3 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCommissionModal(false);
                    setEditingServiceType(null);
                    setCommissionFormData({
                      level1FixedValue: '',
                      level2FixedValue: '',
                      level3FixedValue: '',
                      level1Percentage: '10',
                      level2Percentage: '20',
                      level3Percentage: '25',
                    });
                    setCommissionError('');
                  }}
                >
                  Cancelar
                </Button>
                <Button
                  onClick={handleSaveCommission}
                  disabled={saveCommissionMutation.isPending}
                >
                  {saveCommissionMutation.isPending ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
